/**
 * Responsive image helpers — WebP variants live under /IMAGES/opt/
 * Generated by: npm run optimize:images
 */

export type ResponsiveImageSpec = {
  /** Public path without query, e.g. /IMAGES/HeroRRR.png */
  src: string
  widths: number[]
  /** CSS sizes attribute for srcset selection */
  sizes: string
  /** Intrinsic width for layout stability (optional) */
  width?: number
  /** Intrinsic height for layout stability (optional) */
  height?: number
}

const OPT_PREFIX = '/IMAGES/opt'

function stripExt(path: string): string {
  return path.replace(/\.(png|jpe?g|webp|gif)$/i, '')
}

function baseName(path: string): string {
  const parts = stripExt(path).split('/')
  return parts[parts.length - 1] ?? path
}

function optDirFor(src: string): string {
  const withoutFile = stripExt(src).replace(/^\/IMAGES\//, '')
  const parts = withoutFile.split('/')
  if (parts.length <= 1) return OPT_PREFIX
  parts.pop()
  return `${OPT_PREFIX}/${parts.join('/')}`
}

export function webpSrcSet(src: string, widths: number[]): string {
  const dir = optDirFor(src)
  const name = baseName(src)
  return widths.map((w) => `${dir}/${name}-${w}w.webp ${w}w`).join(', ')
}

export function defaultSrc(src: string, widths: number[]): string {
  const dir = optDirFor(src)
  const name = baseName(src)
  const w = widths[widths.length - 1] ?? widths[0]
  return `${dir}/${name}-${w}w.webp`
}

/** Shrink Unsplash URLs for card thumbnails (~384px display width). */
export function optimizeRemoteImageUrl(url: string, width = 400): string {
  if (!url || !url.includes('images.unsplash.com')) return url
  try {
    const parsed = new URL(url)
    parsed.searchParams.set('w', String(width))
    parsed.searchParams.set('q', '75')
    parsed.searchParams.set('auto', 'format')
    parsed.searchParams.set('fit', 'crop')
    return parsed.toString()
  } catch {
    return url
  }
}

export function optimizeRemoteImageSrcSet(url: string, widths: number[] = [384, 768]): string {
  if (!url || !url.includes('images.unsplash.com')) return ''
  return widths.map((w) => `${optimizeRemoteImageUrl(url, w)} ${w}w`).join(', ')
}

/** Preset specs for common site images */
export const IMAGE_SPECS = {
  hero: {
    src: '/IMAGES/HeroRRR.png',
    widths: [640, 960, 1200],
    sizes: '(max-width: 1024px) 100vw, 1200px',
    width: 1200,
    height: 963,
  },
  mobile3d: {
    src: '/IMAGES/3DMobile.png',
    widths: [448, 896],
    sizes: '(max-width: 1024px) 80vw, 28rem',
    width: 7319,
    height: 7128,
  },
  testimonialAvatar: {
    src: '/IMAGES/4.jpg',
    widths: [80, 160],
    sizes: '80px',
    width: 80,
    height: 80,
  },
  cityCard: {
    src: '/IMAGES/Cities/London.jpg',
    widths: [480, 640, 960],
    sizes: '(max-width: 768px) 50vw, 280px',
    width: 280,
    height: 210,
  },
  logo: {
    src: '/IMAGES/LogoUpdated.png',
    widths: [146, 292],
    sizes: '(max-width: 768px) 120px, 146px',
    width: 499,
    height: 219,
  },
  featureIcon: {
    src: '/IMAGES/HighlyTrusted.png',
    widths: [56, 112],
    sizes: '56px',
  },
  storeIcon: {
    src: '/IMAGES/Playstore.png',
    widths: [40, 80],
    sizes: '20px',
  },
  appleStoreIcon: {
    src: '/IMAGES/apple.png',
    widths: [40, 80],
    sizes: '20px',
    width: 40,
    height: 40,
  },
  cartIcon: {
    src: '/IMAGES/CART.png',
    widths: [32, 64],
    sizes: '16px',
  },
  paperPlane: {
    src: '/IMAGES/PaperAirplane.png',
    widths: [80, 160],
    sizes: '80px',
  },
  esimHero: {
    src: '/IMAGES/eSIM.png',
    widths: [320, 640],
    sizes: '(max-width: 768px) 90vw, 28rem',
  },
  ccid: {
    src: '/IMAGES/CCID.png',
    widths: [240, 480],
    sizes: '(max-width: 768px) 80vw, 20rem',
  },
} satisfies Record<string, ResponsiveImageSpec>

/** Payment badges in the footer — display size for CLS (lazy below fold). */
export const PAYMENT_ICONS = [
  { src: '/IMAGES/gpay.svg', alt: 'Google Pay', width: 48, height: 48 },
  { src: '/IMAGES/applepay.svg', alt: 'Apple Pay', width: 48, height: 48 },
  { src: '/IMAGES/visa.svg', alt: 'Visa', width: 48, height: 32 },
  { src: '/IMAGES/mastercard.svg', alt: 'Mastercard', width: 48, height: 32 },
] as const

/**
 * Desktop-only hero preload. On mobile the hero is `hidden lg:flex`, so
 * preloading HeroRRR there only steals bandwidth from JS and delays FCP/LCP.
 */
export function heroLcpPreloadLinkTag(): string {
  const { src, widths, sizes } = IMAGE_SPECS.hero
  const desktopWidth = widths.includes(960) ? 960 : (widths[widths.length - 1] ?? 640)
  const dir = optDirFor(src)
  const name = baseName(src)
  const href = `${dir}/${name}-${desktopWidth}w.webp`
  const imageSrcSet = webpSrcSet(src, widths)
  return `<link rel="preload" as="image" type="image/webp" href="${href}" imagesrcset="${imageSrcSet}" imagesizes="${sizes}" media="(min-width: 1024px)" fetchpriority="high" />`
}

/**
 * @deprecated Mobile no longer injects a body prerender (display:none still
 * downloaded the image). Kept as empty for any callers; desktop uses preload + React.
 */
export function heroLcpPrerenderMarkup(): string {
  return ''
}

/** Safe no-op — prerender node is no longer injected. */
export function hideHeroLcpPrerender(): void {
  document.getElementById('hero-lcp-prerender')?.classList.add('is-hidden')
}

export function specForCityImage(cityPath: string): ResponsiveImageSpec {
  return {
    src: cityPath,
    widths: [480, 640, 960],
    sizes: '(max-width: 768px) 50vw, 280px',
    width: 280,
    height: 210,
  }
}

export function specForArticleHeroImage(cityPath: string): ResponsiveImageSpec {
  return {
    src: cityPath,
    widths: [480, 640, 960],
    sizes: '(max-width: 768px) 100vw, 896px',
    width: 960,
    height: 640,
  }
}

export function specForLocalImage(src: string, widths: number[], sizes: string): ResponsiveImageSpec {
  return { src, widths, sizes }
}
